import { motion } from '@/lib/motion';
import {
  Briefcase,
  Home,
  Scale,
  Scissors,
  Smile,
  Stethoscope,
  UtensilsCrossed,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const INDUSTRIES = [
  {
    icon: Stethoscope,
    name: 'Medical clinics',
    line: 'Triage routine calls, capture appointment requests, escalate urgent ones.',
    href: '/industries/medical',
  },
  {
    icon: Smile,
    name: 'Dental practices',
    line: 'Every new-patient call answered — even mid-procedure.',
    href: '/industries/dental',
  },
  {
    icon: Wrench,
    name: 'Trades',
    line: "Quote requests captured while you're on the tools.",
    href: '/industries/trades',
  },
  {
    icon: Home,
    name: 'Real estate',
    line: 'Inspection times, listing questions, buyer callbacks — handled.',
    href: '/industries/real-estate',
  },
  {
    icon: Scale,
    name: 'Legal firms',
    line: 'Intake details collected accurately, transferred when it matters.',
    href: '/industries/legal',
  },
  {
    icon: Scissors,
    name: 'Salons & spas',
    line: 'Booking requests taken while your team stays with clients.',
    href: '/industries/salons',
  },
  {
    icon: UtensilsCrossed,
    name: 'Hospitality',
    line: 'Reservations and opening-hours questions answered instantly.',
    href: '/industries/hospitality',
  },
  {
    icon: Briefcase,
    name: 'Professional services',
    line: 'First impressions that sound like your best employee.',
    href: '/industries/professional-services',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export function IndustriesSection(): JSX.Element {
  return (
    <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Built for every business that lives on calls and chats.
          </h2>
          <p className="mt-4 text-muted-foreground">
            One knowledge base, trained on your services, hours, and policies —
            answering on the phone and your website, whatever your industry.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {INDUSTRIES.map((ind) => (
            <motion.div key={ind.name} variants={cardVariants}>
              <Link
                to={ind.href}
                className="group block h-full rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-[var(--brand-accent)]/40 hover:shadow-lg hover:shadow-[var(--brand-accent)]/10 motion-reduce:hover:translate-y-0"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--brand-accent)]/15 to-fuchsia-500/10 text-[var(--brand-accent)] transition-transform group-hover:scale-110 motion-reduce:group-hover:scale-100">
                  <ind.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-semibold">{ind.name}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{ind.line}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
